import api from './api';

/**
 * #403 API console. `/insurers/insurance/api-console/` (`InsurerApiConsoleViewSet`),
 * scoped server-side to the caller's own insurer. The server enforces every gate; the
 * screen only mirrors it. No response ever carries a key or a secret: the signing secret
 * of a webhook endpoint appears once, in the create / rotate response, and is held in
 * component state only.
 */
const URL = '/insurers/insurance/api-console/';

export type ConsoleEnv = 'sandbox' | 'production';

export interface EnvOverview {
  environment: ConsoleEnv;
  configured: boolean;
  status: { state: string; label: string };
  active_api_version: string;
  last_test: { at: string | null; ok: boolean | null };
  last_24h: { calls: number; success: number; success_rate_pct: number | null };
  last_7d: { calls: number; success: number; success_rate_pct: number | null };
  latency_ms: { p50: number | null; p95: number | null };
  top_error_codes: { code: string; count: number }[];
  last_success_at: string | null;
  last_failure_at: string | null;
  endpoints: { endpoint_name: string; calls: number; success_rate_pct: number | null; avg_latency_ms: number; last_call_at: string | null }[];
  has_calls: boolean;
}

export interface LogRow {
  id: string;
  started_at: string;
  environment: ConsoleEnv;
  endpoint_name: string;
  http_method: string;
  status: string;
  response_status_code: number | null;
  latency_ms: number;
  platform_error_code: string;
  error_message: string;
  broker_code: string;
  retry_attempt: number;
}

export interface LogDetail extends LogRow {
  request_url: string;
  request_headers: Record<string, unknown>;
  request_body: unknown;
  request_truncated: boolean;
  response_headers: Record<string, unknown>;
  response_body: unknown;
  response_truncated: boolean;
  retry_chain: { id: string; retry_attempt: number; status: string; response_status_code: number | null; started_at: string }[];
}

export interface Connection {
  broker_code: string;
  relation_status: string;
  environments: Record<ConsoleEnv, { credential_saved: boolean; key_hint: string; updated_at: string | null; last_success_at: string | null; last_error_at: string | null }>;
  api_access: { api_product: string; enabled: boolean; enabled_by_insurer: boolean; key_hint: string }[];
}

export interface ChangeRequest {
  id: string;
  kind: 'go_live' | 'base_url';
  environment: ConsoleEnv;
  status: 'pending' | 'approved' | 'declined' | 'cancelled';
  note: string;
  payload: Record<string, unknown>;
  requested_at: string;
  decided_at: string | null;
  decision_note: string;
}

export interface WebhookEndpoint {
  id: string;
  environment: ConsoleEnv;
  url: string;
  description: string;
  event_types: string[];
  header_names: string[];
  is_active: boolean;
  consecutive_failures: number;
  disabled_reason: string;
  last_triggered_at: string | null;
  has_secret: boolean;
  previous_secret_valid_until: string | null;
  row_version: number;
  /** Present once, on create and on rotate. */
  secret?: string;
}

export interface WebhookDeliveryRow {
  id: string;
  event_id: string;
  event_type: string;
  is_test: boolean;
  status: 'pending' | 'delivered' | 'failed';
  attempt_count: number;
  next_attempt_at: string;
  last_status_code: number | null;
  last_latency_ms: number | null;
  last_error: string;
  last_response_snippet: string;
  created_at: string;
}

export interface WebhookEventInfo {
  type: string;
  version: string;
  description: string;
  fields: string[];
  sample: Record<string, string>;
}

export interface WebhookEndpointInput {
  environment: ConsoleEnv;
  url: string;
  description?: string;
  event_types: string[];
  headers?: Record<string, string>;
  is_active?: boolean;
}

export const insurerApiConsoleService = {
  async overview(): Promise<Record<ConsoleEnv, EnvOverview>> {
    return (await api.get(`${URL}overview/`)).data;
  },
  async logs(params: Record<string, string>): Promise<{ count: number; results: LogRow[] }> {
    return (await api.get(`${URL}logs/`, { params })).data;
  },
  async logDetail(id: string): Promise<LogDetail> {
    return (await api.get(`${URL}logs/${id}/`)).data;
  },
  exportUrl(params: Record<string, string>): string {
    const q = new URLSearchParams(params).toString();
    return `${URL}logs/export/${q ? `?${q}` : ''}`;
  },
  async exportLogs(params: Record<string, string>): Promise<Blob> {
    return (await api.get(`${URL}logs/export/`, { params, responseType: 'blob' })).data;
  },
  async connections(): Promise<Connection[]> {
    return (await api.get(`${URL}connections/`)).data;
  },
  async requests(): Promise<ChangeRequest[]> {
    return (await api.get(`${URL}requests/`)).data;
  },
  async createRequest(body: Record<string, unknown>): Promise<ChangeRequest> {
    return (await api.post(`${URL}requests/`, body)).data;
  },
  async cancelRequest(id: string): Promise<ChangeRequest> {
    return (await api.post(`${URL}requests/${id}/cancel/`)).data;
  },
  async webhooks(): Promise<WebhookEndpoint[]> {
    return (await api.get(`${URL}webhooks/`)).data;
  },
  async webhookEvents(): Promise<{ events: WebhookEventInfo[]; signature: { header: string; format: string; tolerance_seconds: number; note: string } }> {
    return (await api.get(`${URL}webhooks/events/`)).data;
  },
  async createWebhook(body: WebhookEndpointInput): Promise<WebhookEndpoint> {
    return (await api.post(`${URL}webhooks/`, body)).data;
  },
  async updateWebhook(id: string, rowVersion: number, body: Partial<WebhookEndpointInput>): Promise<WebhookEndpoint> {
    return (await api.patch(`${URL}webhooks/${id}/`, { ...body, row_version: rowVersion })).data;
  },
  async deleteWebhook(id: string): Promise<void> {
    await api.delete(`${URL}webhooks/${id}/`);
  },
  async rotateSecret(id: string): Promise<WebhookEndpoint> {
    return (await api.post(`${URL}webhooks/${id}/rotate-secret/`)).data;
  },
  async testWebhook(id: string): Promise<WebhookDeliveryRow> {
    return (await api.post(`${URL}webhooks/${id}/test/`)).data;
  },
  async deliveries(id: string): Promise<{ count: number; results: WebhookDeliveryRow[] }> {
    return (await api.get(`${URL}webhooks/${id}/deliveries/`)).data;
  },
  async resend(deliveryId: string): Promise<WebhookDeliveryRow> {
    return (await api.post(`${URL}deliveries/${deliveryId}/resend/`)).data;
  },
};
